import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { LuX } from "react-icons/lu";

import CuttingPickerCard, {
  CardQtyRow,
  CardRateRow,
  rowLabelClass,
} from "../cuttingLines/CuttingPickerCard";
import CuttingPickerDialog, {
  PickerFilter,
  matchesFilter,
  noFilter,
} from "../cuttingLines/CuttingPickerDialog";
import cx from "../ui/cx";
import { useAppSelector } from "../../app/hooks";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { lineName } from "../../static/cuttingLine";
import { formatQty } from "../../static/materialLine";
import { isNumberSize } from "../../static/itemSize";
import { hasPermission, permission } from "../../static/permission";
import { rateText } from "../../static/processRate";
import { compactSelectStyles } from "../../static/styles";
import { sourceLabel, vmcCardKey, vmcLineKey } from "../../static/vmcLine";
import { CuttingRateSource } from "../../types/cuttingOrder";
import { IVmcLineInput, VmcSource } from "../../types/vmcOrder";

// One size of a card (an item without sizes has one, without a name): what a line of it copies, the pieces of the card's
// kind ready for the order, and on an order being edited what its own line has received.
export type VmcCardSize = {
  // vmcOptionKey: the item, the size and the card's kind.
  key: string;
  size_id: number | null;
  sizeName: string | null;
  // Shown only; missing when the item page has none (VMC needs none).
  cutLength: number | null;
  // Only for a user who may see rates.
  rate?: number;
  rateSource?: CuttingRateSource;
  // Pieces still to come on the party's other open orders.
  pending: number;
  // Pieces of the card's kind ready for this order (an order being edited counts its own lines as ready).
  ready: number;
  // On an order being edited: its own line's qty as saved, what it has received, and whether a receive closed it.
  savedQty: number;
  received: number;
  closed: boolean;
};

// One card per item and kind of pieces (cut or turned) ready that the party can be asked to machine, with its sizes,
// plus, on an order being edited, its own lines that are no longer offered. An item with both kinds ready has two cards.
// The form works them out; the picker only shows and picks them.
export type VmcCardProps = {
  // vmcCardKey: the item and the kind.
  key: string;
  item_id: number;
  source: VmcSource;
  // The item's category (0 without one): the form opens the picker on one category's cards.
  categoryId: number;
  code: string;
  // The parent's code for a sub item, else the item's description.
  subtitle: string | null;
  // The page whose item holds it: a sub item is edited on its parent's page.
  editItemId: number;
  imageUrl: string | null;
  materialName: string | null;
  sizes: VmcCardSize[];
};

type SizeOption = { value: string; label: string; size: VmcCardSize };

// The app's select sized like the card's boxes; its menu opens over the dialog.
const cardSelectStyles = compactSelectStyles<SizeOption>();

const noLines: IVmcLineInput[] = [];

// A line received against the order being edited stays on it, with its size.
const isReceived = (size: VmcCardSize) => size.received > 0 || size.closed;

// The most a line of this size may have: the pieces of its kind ready, or what it already had or received (the server
// lets a line keep those).
export const vmcCap = (ready: number, savedQty: number, received: number) =>
  Math.max(ready, savedQty, received);

const isOverCap = (size: VmcCardSize, qty: string | undefined) =>
  Number(qty) > vmcCap(size.ready, size.savedQty, size.received);

// The ready line of a card or a size: green, red once a typed qty is above what is ready.
export const readyLineClass = (over: boolean) =>
  cx(
    "text-xs font-semibold leading-5",
    over ? "text-red-600" : "text-green-700"
  );

// A size in the select, with the size unit: "120mm". A size still written as text before sizes became numbers is shown
// as it was written.
const sizeLabel = (size: VmcCardSize, unit: string) =>
  !size.sizeName
    ? "સાઈઝ વગર"
    : isNumberSize(size.sizeName)
    ? `${size.sizeName}${unit}`
    : size.sizeName;

type EditorProps = {
  card: VmcCardProps;
  // The size being edited, and its line once it has a qty.
  size: VmcCardSize;
  line: IVmcLineInput | undefined;
  rate: string;
  // An item with more sizes picks the size to edit here, from these.
  withSelect: boolean;
  sizeOptions: SizeOption[];
  withRates: boolean;
  qtyRef: React.Ref<HTMLInputElement>;
  onSelectSize: (key: string) => void;
  onChangeRate: (rate: string) => void;
  onChangeQty: (qty: string) => void;
};

// The card's editor: on an item with more sizes the size being edited, its pieces still pending and its pieces ready,
// then (for a user who may see rates) its rate, which starts at the size's rate, and its qty.
const SizeEditor = ({
  card,
  size,
  line,
  rate,
  withSelect,
  sizeOptions,
  withRates,
  qtyRef,
  onSelectSize,
  onChangeRate,
  onChangeQty,
}: EditorProps) => {
  const name = `${lineName(card.code, size.sizeName)} • ${sourceLabel(card.source)}`;
  const isLocked = !!line && isReceived(size);

  return (
    <div className="space-y-2">
      {/* On a narrow card (phones) the select goes under its label rather than shrinking, so the size stays readable. */}
      {withSelect && (
        <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
          <span className={rowLabelClass}>સાઈઝ:</span>
          <span className="relative ml-auto w-28 shrink-0">
            <Select<SizeOption, false>
              aria-label={`${card.code} • ${sourceLabel(card.source)} સાઈઝ`}
              options={sizeOptions}
              value={
                sizeOptions.find((option) => option.value === size.key) || null
              }
              onChange={(option) => {
                if (option && option.value !== size.key) {
                  onSelectSize(option.value);
                }
              }}
              isSearchable={false}
              menuPortalTarget={document.body}
              menuPosition="fixed"
              menuPlacement="auto"
              styles={cardSelectStyles}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </span>
        </div>
      )}

      {withRates && (
        <CardRateRow
          label={`${name} ભાવ`}
          value={rate}
          disabled={isLocked}
          title={isLocked ? "મળી ગયેલ આઈટમ નો ભાવ બદલી શકાશે નહીં" : undefined}
          onChange={onChangeRate}
        />
      )}

      {withSelect && size.pending > 0 && (
        <div className="text-xs font-semibold leading-5 text-orange-700">
          ઓર્ડર બાકી: {formatQty(size.pending)}
        </div>
      )}

      {withSelect && (
        <div className={readyLineClass(isOverCap(size, line?.qty))}>
          તૈયાર: {formatQty(size.ready)}
        </div>
      )}

      {isLocked && (
        <div className="flex items-center justify-between gap-2 text-xs">
          <span className={rowLabelClass}>મળેલ:</span>
          <span className="text-sm leading-4 text-gray-600">
            {formatQty(size.received)}
            {size.closed && " • બંધ"}
          </span>
        </div>
      )}

      <CardQtyRow
        label={`${name} જથ્થો`}
        value={line?.qty ?? ""}
        inputRef={qtyRef}
        onChange={onChangeQty}
      />
    </div>
  );
};

type AddedProps = {
  card: VmcCardProps;
  rows: IVmcLineInput[];
  sizeByKey: Map<string, VmcCardSize>;
  activeKey: string;
  sizeUnit: string;
  onSelect: (key: string) => void;
  onRemove: (key: string) => void;
};

// The sizes the card puts on the order, as "120mm × 100", at its end. A click edits one in the editor; the × takes it
// off the order, except a received one.
const AddedSizes = ({
  card,
  rows,
  sizeByKey,
  activeKey,
  sizeUnit,
  onSelect,
  onRemove,
}: AddedProps) => (
  <div className="flex flex-wrap gap-1.5 border-t border-dashed border-gray-300 pt-2.5">
    {rows.map((line) => {
      const key = vmcLineKey(line);
      const size = sizeByKey.get(key)!;
      const name = `${lineName(card.code, size.sizeName)} • ${sourceLabel(card.source)}`;
      const canRemove = !isReceived(size);
      const isActive = key === activeKey;

      return (
        <span
          key={key}
          className={cx(
            "inline-flex items-center rounded-full border text-xs font-semibold leading-5",
            isActive
              ? "border-[#113F67] bg-[#113F67] text-white"
              : "border-gray-300 bg-white text-[#113F67]"
          )}
        >
          <button
            type="button"
            title="આ સાઈઝ બદલો"
            aria-label={`${name} બદલો`}
            onClick={() => onSelect(key)}
            className={cx("py-0.5 pl-2", canRemove ? "pr-1" : "pr-2")}
          >
            {sizeLabel(size, sizeUnit)} × {line.qty ? formatQty(line.qty) : "-"}
          </button>
          {canRemove && (
            <button
              type="button"
              title="આ સાઈઝ કાઢી નાખો"
              aria-label={`${name} કાઢી નાખો`}
              onClick={() => onRemove(key)}
              className={cx(
                "mr-1 rounded-full p-0.5",
                isActive
                  ? "text-white/80 hover:bg-white/20 hover:text-white"
                  : "text-gray-400 hover:bg-red-50 hover:text-red-600"
              )}
            >
              <LuX className="h-3 w-3" />
            </button>
          )}
        </span>
      );
    })}
  </div>
);

type CardProps = {
  card: VmcCardProps;
  // The item's lines on the picker, in the form's order.
  lines: IVmcLineInput[];
  withRates: boolean;
  sizeUnit: string;
  canOpenItem: boolean;
  onAddLine: (
    itemId: number,
    sizeId: number | null,
    source: VmcSource,
    rate: string,
    qty: string
  ) => void;
  onRemoveLines: (keys: string[]) => void;
  onChangeRate: (key: string, rate: string) => void;
  onChangeQty: (key: string, qty: string) => void;
};

// A card is drawn again only when something it gets changes. Its lines count as unchanged while each line is the same,
// so typing in one card doesn't redraw the others.
const sameCardProps = (prev: CardProps, next: CardProps) =>
  (Object.keys(next) as (keyof CardProps)[]).every((name) =>
    name === "lines"
      ? prev.lines.length === next.lines.length &&
        prev.lines.every((line, index) => line === next.lines[index])
      : prev[name] === next[name]
  );

// One card per item and kind of pieces, like the turning order picker's card, its ready line naming the kind. Ticking it
// opens its editor on its first size; an item with more sizes picks the size to edit there. A size goes on the order
// once it has a qty, and comes off when its qty is cleared; the sizes on the order are listed at the end of the card.
// Every size it shows has pieces of its kind ready, so all can be picked.
const ItemCard = React.memo(
  ({
    card,
    lines,
    withRates,
    sizeUnit,
    canOpenItem,
    onAddLine,
    onRemoveLines,
    onChangeRate,
    onChangeQty,
  }: CardProps) => {
    const sizeByKey = useMemo(
      () => new Map(card.sizes.map((size) => [size.key, size])),
      [card]
    );

    // The size select's options.
    const sizeOptions = useMemo(
      () =>
        card.sizes.map(
          (size): SizeOption => ({
            value: size.key,
            label: sizeLabel(size, sizeUnit),
            size,
          })
        ),
      [card, sizeUnit]
    );

    // The item's lines of a size the card doesn't have (the party doesn't offer it) stay off it, as before.
    const rows = lines.filter((line) => sizeByKey.has(vmcLineKey(line)));
    const lineByKey = new Map(rows.map((line) => [vmcLineKey(line), line]));

    const single = card.sizes.length === 1 ? card.sizes[0] : null;
    const firstSize = card.sizes[0];
    const isSelected = rows.length > 0;
    const isLocked = rows.some((line) =>
      isReceived(sizeByKey.get(vmcLineKey(line))!)
    );
    const pending = card.sizes.reduce((sum, size) => sum + size.pending, 0);
    const ready = card.sizes.reduce((sum, size) => sum + size.ready, 0);
    const isOver = rows.some((line) =>
      isOverCap(sizeByKey.get(vmcLineKey(line))!, line.qty)
    );

    // An open card shows its editor: one with lines opens with them, and ticking opens one.
    const [isOpen, setIsOpen] = useState(isSelected);
    // The size in the editor: the one picked, else the first line's, else the first.
    const [active, setActive] = useState<string | null>(null);
    // A rate typed for a size before its qty, or kept when its qty was cleared, by size.
    const [drafts, setDrafts] = useState<Record<string, string>>({});

    const activeKey =
      active && sizeByKey.has(active)
        ? active
        : rows.length > 0
        ? vmcLineKey(rows[0])
        : firstSize?.key ?? null;
    const activeSize = activeKey ? sizeByKey.get(activeKey) : undefined;
    const activeLine = activeKey ? lineByKey.get(activeKey) : undefined;
    const activeRate = activeLine
      ? activeLine.rate
      : activeKey && drafts[activeKey] !== undefined
      ? drafts[activeKey]
      : rateText(activeSize?.rate);

    const showEditor = (isOpen || isSelected) && !!activeSize;
    const canToggle = showEditor ? !isLocked : !!firstSize;

    const qtyRef = useRef<HTMLInputElement>(null);
    // Set when the user opens the card or picks a size, so the qty box takes the focus once it is there. Cards open
    // when the picker opens leave the focus alone.
    const focusQty = useRef(false);

    useEffect(() => {
      if (focusQty.current && qtyRef.current) {
        focusQty.current = false;
        qtyRef.current.focus();
      }
    });

    const handleToggle = () => {
      if (!canToggle) return;

      if (showEditor) {
        onRemoveLines(rows.map(vmcLineKey));
        setIsOpen(false);
        setActive(null);
        setDrafts({});
      } else {
        focusQty.current = true;
        setIsOpen(true);
        setActive(firstSize?.key ?? null);
      }
    };

    const handleSelectSize = (key: string) => {
      focusQty.current = true;
      setActive(key);
    };

    // Keeps a line's rate for when its size is given a qty again.
    const keepRate = (key: string) => {
      const line = lineByKey.get(key);

      if (line) setDrafts((current) => ({ ...current, [key]: line.rate }));
    };

    // The first qty puts the size on the order, at the rate in the box; clearing it takes the size off again, except
    // a received line.
    const handleQty = (qty: string) => {
      if (!activeKey || !activeSize) return;

      if (!activeLine) {
        if (qty !== "") {
          onAddLine(card.item_id, activeSize.size_id, card.source, activeRate, qty);
        }
      } else if (qty === "" && !isReceived(activeSize)) {
        keepRate(activeKey);
        onRemoveLines([activeKey]);
      } else {
        onChangeQty(activeKey, qty);
      }
    };

    const handleRate = (rate: string) => {
      if (!activeKey) return;

      if (activeLine) {
        onChangeRate(activeKey, rate);
      } else {
        setDrafts((current) => ({ ...current, [activeKey]: rate }));
      }
    };

    const handleRemove = (key: string) => {
      keepRate(key);
      onRemoveLines([key]);
    };

    return (
      <CuttingPickerCard
        code={card.code}
        sizeName={single?.sizeName ?? null}
        subtitle={card.subtitle}
        imageUrl={card.imageUrl}
        editItemId={card.editItemId}
        canOpenItem={canOpenItem}
        materialName={card.materialName}
        cutLength={single ? single.cutLength : null}
        lengthMissing={false}
        sizeUnit={sizeUnit}
        rate={single && withRates ? single.rate : undefined}
        rateSource={single?.rateSource}
        extra={
          <>
            {pending > 0 && (
              <div className="text-xs font-semibold leading-5 text-orange-700">
                ઓર્ડર બાકી: {formatQty(pending)}
              </div>
            )}
            <div className={readyLineClass(isOver)}>
              {sourceLabel(card.source)}: {formatQty(ready)}
            </div>
          </>
        }
        isOpen={showEditor}
        canToggle={canToggle}
        title={isLocked ? "મળી ગયેલ આઈટમ કાઢી શકાશે નહીં" : undefined}
        onToggle={handleToggle}
        editor={
          showEditor &&
          activeKey &&
          activeSize && (
            <>
              <SizeEditor
                card={card}
                size={activeSize}
                line={activeLine}
                rate={activeRate}
                withSelect={!single}
                sizeOptions={sizeOptions}
                withRates={withRates}
                qtyRef={qtyRef}
                onSelectSize={handleSelectSize}
                onChangeRate={handleRate}
                onChangeQty={handleQty}
              />

              {!single && rows.length > 0 && (
                <AddedSizes
                  card={card}
                  rows={rows}
                  sizeByKey={sizeByKey}
                  activeKey={activeKey}
                  sizeUnit={sizeUnit}
                  onSelect={handleSelectSize}
                  onRemove={handleRemove}
                />
              )}
            </>
          )
        }
      />
    );
  },
  sameCardProps
);

ItemCard.displayName = "ItemCard";

type Props = {
  cards: VmcCardProps[];
  cardsLoading: boolean;
  // The category the cards belong to, after the title.
  categoryLabel?: string;
  // The form's lines when the picker opens; the picker hands its lines back when it closes.
  initialLines: IVmcLineInput[];
  // Rates show only for a user who may see them.
  withRates: boolean;
  // An in-house party is offered items without a VMC rate too.
  inhouse: boolean;
  sizeUnit: string;
  onClose: (lines: IVmcLineInput[]) => void;
};

// Picks the pieces to machine as cards, like the turning order picker: one card per item and kind of pieces, and a line
// per size given a qty in its card. The form opens it on one category's cards; the lines of the order's other categories pass through
// untouched. There is no separate save; closing the picker in any way hands its lines to the form. Mount it only while
// it is open.
const VmcItemPickerModal = ({
  cards,
  cardsLoading,
  categoryLabel,
  initialLines,
  withRates,
  inhouse,
  sizeUnit,
  onClose,
}: Props) => {
  const loggingUser = useAppSelector(getLoggingUser);
  const canOpenItem = hasPermission(
    loggingUser?.permissions,
    permission.itemsUpdate
  );

  const [lines, setLines] = useState<IVmcLineInput[]>(() => initialLines);
  // The filter in use: it changes only on "ફિલ્ટર લાગુ કરો" (or Enter).
  const [filter, setFilter] = useState<PickerFilter>(noFilter);

  // The items already on the form come first. They keep their place while picking, so no card moves under the pointer.
  const [openedWith] = useState(
    () =>
      new Set(initialLines.map((line) => vmcCardKey(line.item_id, line.source)))
  );

  const linesByCard = useMemo(() => {
    const byCard = new Map<string, IVmcLineInput[]>();

    lines.forEach((line) => {
      const key = vmcCardKey(line.item_id, line.source);

      byCard.set(key, [...(byCard.get(key) || []), line]);
    });

    return byCard;
  }, [lines]);

  // A new line comes right after its card's other lines, else after the item's lines of the other kind, so its sizes
  // stay together on the form. It comes with its first qty and the rate in the card's box.
  const handleAddLine = useCallback(
    (
      itemId: number,
      sizeId: number | null,
      source: VmcSource,
      rate: string,
      qty: string
    ) => {
      setLines((current) => {
        const line: IVmcLineInput = {
          item_id: itemId,
          size_id: sizeId,
          source,
          qty,
          rate,
          refresh: false,
        };
        const key = vmcLineKey(line);

        if (current.some((item) => vmcLineKey(item) === key)) {
          return current;
        }

        const cardKey = vmcCardKey(itemId, source);
        const lastOfCard = current
          .map((item) => vmcCardKey(item.item_id, item.source))
          .lastIndexOf(cardKey);
        const last =
          lastOfCard !== -1
            ? lastOfCard
            : current.map((item) => item.item_id).lastIndexOf(itemId);

        return last === -1
          ? [...current, line]
          : [...current.slice(0, last + 1), line, ...current.slice(last + 1)];
      });
    },
    []
  );

  const handleRemoveLines = useCallback((keys: string[]) => {
    setLines((current) =>
      current.filter((line) => !keys.includes(vmcLineKey(line)))
    );
  }, []);

  const handleChangeRate = useCallback((key: string, rate: string) => {
    setLines((current) =>
      current.map((line) =>
        vmcLineKey(line) === key ? { ...line, rate } : line
      )
    );
  }, []);

  const handleChangeQty = useCallback((key: string, qty: string) => {
    setLines((current) =>
      current.map((line) =>
        vmcLineKey(line) === key ? { ...line, qty } : line
      )
    );
  }, []);

  // The cards already on the form first, then the rest, as the filter finds them.
  const visibleCards = useMemo(() => {
    const found = cards.filter((card) =>
      matchesFilter(filter, card.item_id, card.code)
    );

    return [
      ...found.filter((card) => openedWith.has(card.key)),
      ...found.filter((card) => !openedWith.has(card.key)),
    ];
  }, [cards, openedWith, filter]);

  // The lines of the cards shown: the order's other categories don't count here.
  const pickedCount = useMemo(() => {
    const cardKeys = new Set(cards.map((card) => card.key));

    return lines.filter((line) =>
      cardKeys.has(vmcCardKey(line.item_id, line.source))
    ).length;
  }, [cards, lines]);

  const handleClose = () => onClose(lines);

  return (
    <CuttingPickerDialog
      categoryLabel={categoryLabel}
      onFilter={setFilter}
      isLoading={cardsLoading && cards.length === 0}
      emptyLabel={
        visibleCards.length > 0
          ? null
          : cards.length === 0
          ? inhouse
            ? "VMC માટે કોઈ કટીંગ કે ટર્નિંગ તૈયાર આઈટમ નથી, પહેલા કટીંગ કે ટર્નિંગ રિસીવ કરો"
            : "આ પાર્ટી માટે VMC ભાવ વાળી કોઈ કટીંગ કે ટર્નિંગ તૈયાર આઈટમ નથી, પહેલા કટીંગ કે ટર્નિંગ રિસીવ કરો અથવા આઈટમ કે પાર્ટી ના ભાવ માં VMC ભાવ ઉમેરો"
          : "ફિલ્ટર મુજબ કોઈ આઈટમ મળી નથી"
      }
      pickedCount={pickedCount}
      onClose={handleClose}
    >
      {visibleCards.map((card) => (
        <ItemCard
          key={card.key}
          card={card}
          lines={linesByCard.get(card.key) || noLines}
          withRates={withRates}
          sizeUnit={sizeUnit}
          canOpenItem={canOpenItem}
          onAddLine={handleAddLine}
          onRemoveLines={handleRemoveLines}
          onChangeRate={handleChangeRate}
          onChangeQty={handleChangeQty}
        />
      ))}
    </CuttingPickerDialog>
  );
};

export default VmcItemPickerModal;
